import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const ContractUpload = ({ open, onClose, onUploadComplete, portfolioId }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleUpload = useCallback(
    async (file) => {
      if (!file) return;

      const nameLc = file.name.toLowerCase();
      const accepted = ['.pdf', '.xlsb', '.xlsm', '.xlsx'];
      if (!accepted.some((ext) => nameLc.endsWith(ext))) {
        setUploadError('Accepted: PDF contracts or Excel financial models (.xlsb/.xlsm/.xlsx)');
        setSelectedFile(null);
        return;
      }

      const isModel = ['.xlsb', '.xlsm', '.xlsx'].some((e) => nameLc.endsWith(e));
      if (isModel && !portfolioId) {
        setUploadError('Open a portfolio first — financial models attach to a portfolio.');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setUploading(true);
      setUploadError(null);

      const mkForm = () => {
        const fd = new FormData();
        fd.append('file', file);
        if (portfolioId) fd.append('portfolio_id', portfolioId);
        return fd;
      };

      try {
        // Excel → standalone financial-models module (own schema).
        if (isModel) {
          const r = await axios.post(
            `${API_BASE_URL}/api/portfolios/${portfolioId}/financial-models/upload`,
            mkForm(), { headers: { 'Content-Type': 'multipart/form-data' } });
          onUploadComplete(r.data, 'model');
        } else {
          // PDF — could be an AEMO settlement invoice or a contract. Try the
          // invoice pipeline first (it sniffs the content before storing
          // anything); if it reports the file isn't an invoice, fall back to
          // the contracts pipeline. This keeps a single upload entry for all
          // document PDFs.
          let handled = false;
          if (portfolioId) {
            try {
              const r = await axios.post(
                `${API_BASE_URL}/api/portfolios/${portfolioId}/invoices/upload`,
                mkForm(), { headers: { 'Content-Type': 'multipart/form-data' } });
              onUploadComplete(r.data, 'invoice');
              handled = true;
            } catch (err) {
              // Only fall through when the sniff said "not an invoice".
              // A real parse error (e.g. invoice with no readable period)
              // should surface, not be misfiled as a contract.
              if (!err.response?.data?.not_invoice) throw err;
            }
          }
          if (!handled) {
            const r = await axios.post(
              `${API_BASE_URL}/api/contracts/upload`,
              mkForm(), { headers: { 'Content-Type': 'multipart/form-data' } });
            onUploadComplete(r.data, 'contract');
          }
        }
        setSelectedFile(null);
        onClose();
      } catch (err) {
        setUploadError(
          err.response?.data?.error || 'Upload failed. Please try again.'
        );
      } finally {
        setUploading(false);
      }
    },
    [onUploadComplete, onClose, portfolioId]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleUpload(file);
    },
    [handleUpload]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (file) handleUpload(file);
      e.target.value = '';
    },
    [handleUpload]
  );

  const handleClose = () => {
    if (!uploading) {
      setUploadError(null);
      setSelectedFile(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '14px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontWeight: 700,
          letterSpacing: '-0.01em',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CloudUploadIcon sx={{ fontSize: 22, color: 'primary.main' }} />
          Upload Document
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={uploading}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 3 }}>
        {/* Drop Zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: '1.5px dashed',
            borderColor: dragOver
              ? 'primary.main'
              : uploading
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(255, 255, 255, 0.10)',
            borderRadius: '12px',
            p: 3.5,
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            bgcolor: dragOver
              ? 'rgba(217, 138, 43, 0.08)'
              : '#fafbfc',
            ...(dragOver && {
              boxShadow:
                '0 0 30px rgba(217, 138, 43, 0.08), inset 0 0 30px rgba(217, 138, 43, 0.02)',
            }),
            '&:hover': uploading
              ? {}
              : {
                  borderColor: 'rgba(217, 138, 43, 0.4)',
                  bgcolor: 'rgba(217, 138, 43, 0.02)',
                },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsb,.xlsm,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {uploading ? (
            <Box>
              <PictureAsPdfIcon
                sx={{ fontSize: 36, color: 'primary.main', mb: 1 }}
              />
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', mb: 1.5, fontWeight: 500 }}
              >
                Uploading {selectedFile?.name}...
              </Typography>
              <LinearProgress
                sx={{
                  maxWidth: 280,
                  mx: 'auto',
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(217, 138, 43, 0.12)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, #D98A2B, #E9B065)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s linear infinite',
                  },
                }}
              />
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon
                sx={{
                  fontSize: 36,
                  color: 'text.secondary',
                  mb: 1,
                  opacity: 0.5,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', mb: 0.5, fontWeight: 500 }}
              >
                Drag & drop a contract or AEMO invoice (PDF), or financial model (Excel) here
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 2 }}
              >
                or click to browse files
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                sx={{
                  borderColor: 'rgba(217, 138, 43, 0.30)',
                  color: 'text.secondary',
                  fontWeight: 600,
                  px: 2.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: 'rgba(217, 138, 43, 0.08)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Select file
              </Button>
            </Box>
          )}
        </Box>

        {/* Upload Error */}
        {uploadError && (
          <Alert
            severity="error"
            onClose={() => setUploadError(null)}
            sx={{ mt: 2, border: '1px solid rgba(229, 56, 79, 0.2)' }}
          >
            {uploadError}
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractUpload;
